import {
  ParameterDataType,
  ParameterDisplayType,
} from '../../../redux/Parameters/ParameterInterface';

type PrimitiveSelection = string | number | boolean | Date | null | undefined;

export interface ValueValidationContext {
  dataType?: ParameterDataType | string;
  values?: Array<string | number>;
  minValue?: PrimitiveSelection;
  maxValue?: PrimitiveSelection;
}

export type NormalizedUserSelection =
  | { kind: 'none'; value: null }
  | { kind: 'normal'; value: PrimitiveSelection }
  | { kind: 'array'; values: PrimitiveSelection[] }
  | { kind: 'range'; values: PrimitiveSelection[] };

const toDateEpoch = (value: PrimitiveSelection) => {
  if (value === null || value === undefined || value === '') return NaN;
  return new Date(value as string | number | Date).getTime();
};

const toFiniteNumber = (value: PrimitiveSelection) => {
  if (value === null || value === undefined || value === '') return NaN;
  return Number(value);
};

export const isValueInAllowedValues = (
  value: PrimitiveSelection,
  dataType: ParameterDataType | string,
  values: Array<string | number> = [],
): boolean => {
  if (!values.length) return true;

  if (dataType === 'number' || dataType === 'decimal') {
    return values.map(Number).includes(Number(value));
  }

  if (dataType === 'date') {
    const epoch = toDateEpoch(value);
    return values.some((v) => toDateEpoch(v) === epoch);
  }

  return values.includes(value as string | number);
};

export const isValueWithinBounds = (
  value: PrimitiveSelection,
  dataType: ParameterDataType | string,
  minValue?: PrimitiveSelection,
  maxValue?: PrimitiveSelection,
): boolean => {
  if (
    minValue === undefined ||
    minValue === null ||
    minValue === '' ||
    maxValue === undefined ||
    maxValue === null ||
    maxValue === ''
  ) {
    return true;
  }

  if (dataType === 'number' || dataType === 'decimal') {
    const num = toFiniteNumber(value);
    const min = toFiniteNumber(minValue);
    const max = toFiniteNumber(maxValue);
    if ([num, min, max].some((n) => Number.isNaN(n))) return false;
    return num >= min && num <= max;
  }

  if (dataType === 'date') {
    const epoch = toDateEpoch(value);
    const min = toDateEpoch(minValue);
    const max = toDateEpoch(maxValue);
    if ([epoch, min, max].some((n) => Number.isNaN(n))) return false;
    return epoch >= min && epoch <= max;
  }

  return true;
};

export const isValidParameterValue = (
  value: PrimitiveSelection,
  context: ValueValidationContext,
): boolean => {
  const { dataType = 'text', values = [], minValue, maxValue } = context;
  if (value === undefined || value === null || value === '') return false;

  if (values.length) {
    return isValueInAllowedValues(value, dataType, values);
  }

  return isValueWithinBounds(value, dataType, minValue, maxValue);
};

export const sanitizeParameterSelections = (
  input: PrimitiveSelection[],
  context: ValueValidationContext,
): PrimitiveSelection[] => {
  return input.filter((value) => value !== '(All)' && isValidParameterValue(value, context));
};

export const resolveParameterDefaultValue = (
  defaultValue: PrimitiveSelection,
  context: ValueValidationContext,
): PrimitiveSelection => {
  return isValidParameterValue(defaultValue, context) ? defaultValue : null;
};

export const normalizeUserSelection = (
  displayType: ParameterDisplayType | string,
  rawSelection: any,
): NormalizedUserSelection => {
  if (rawSelection === undefined || rawSelection === null || rawSelection === '') {
    return { kind: 'none', value: null };
  }

  const normalizedArray = Array.isArray(rawSelection) ? rawSelection : [rawSelection];
  const normalizedValue = normalizedArray[0];

  if (displayType.includes('checkbox')) {
    return { kind: 'array', values: normalizedArray };
  }

  if (displayType.includes('range')) {
    return { kind: 'range', values: normalizedArray };
  }

  if (
    displayType.includes('radio') ||
    displayType.includes('input') ||
    displayType.includes('slider') ||
    displayType.includes('calendar') ||
    displayType === 'switch'
  ) {
    return {
      kind: 'normal',
      value: normalizedValue,
    };
  }

  return Array.isArray(rawSelection)
    ? { kind: 'array', values: normalizedArray }
    : { kind: 'normal', value: normalizedValue };
};
